import React from "react";
import ReactDOM from "react-dom";
import cn from "./helpers/cn";

export type DialogMaxWidth = "xs" | "sm" | "md" | "lg" | "xl" | false;
export type DialogScroll = "body" | "paper";
export type DialogCloseReason = "backdropClick" | "escapeKeyDown";

export interface DialogContextValue {
  onClose?: (event: React.SyntheticEvent | Event, reason: DialogCloseReason) => void;
  scroll: DialogScroll;
}

export const DialogContext = React.createContext<DialogContextValue | null>(null);

interface DialogProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClose"> {
  open: boolean;
  onClose?: (event: React.SyntheticEvent | Event, reason: DialogCloseReason) => void;
  fullScreen?: boolean;
  fullWidth?: boolean;
  maxWidth?: DialogMaxWidth;
  scroll?: DialogScroll;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  disableEnforceFocus?: boolean;
  PaperProps?: React.HTMLAttributes<HTMLDivElement>;
  BackdropProps?: React.HTMLAttributes<HTMLDivElement>;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  children?: React.ReactNode;
}

// MUI v4 Dialog maxWidth:
// xs = max(breakpoints.xs, 444) = 444; sm = 600; md = 960; lg = 1280; xl = 1920.
const maxWidthMap: Record<Exclude<DialogMaxWidth, false>, string> = {
  xs: "max-w-[444px]",
  sm: "max-w-[600px]",
  md: "max-w-[960px]",
  lg: "max-w-[1280px]",
  xl: "max-w-[1920px]",
};

const Dialog = React.forwardRef<HTMLDivElement, DialogProps>((props, ref) => {
  const {
    className,
    open,
    onClose,
    fullScreen = false,
    fullWidth = false,
    maxWidth = "sm",
    scroll = "paper",
    disableBackdropClick = false,
    disableEscapeKeyDown = false,
    // Desestruturado apenas para não vazar no spread `{...other}` (DOM).
    // Este Dialog não implementa focus trap, então a prop é só para
    // compatibilidade de API com o MUI Dialog.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    disableEnforceFocus = false,
    PaperProps,
    BackdropProps,
    children,
    ...other
  } = props;

  // Bloqueia scroll do body e captura Escape enquanto aberto.
  React.useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !disableEscapeKeyDown) {
        onClose?.(event, "escapeKeyDown");
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, disableEscapeKeyDown]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Só dispara quando o clique veio do próprio backdrop (não dos filhos).
    if (event.target !== event.currentTarget) return;
    if (disableBackdropClick) return;
    onClose?.(event, "backdropClick");
  };

  const ctx: DialogContextValue = { onClose, scroll };

  // scroll="paper": container central, paper limita altura, conteúdo rolável.
  // scroll="body":  container rolável, paper flui no fluxo da página.
  const isPaperScroll = scroll === "paper";

  // Renderiza via portal no body para escapar de ancestrais com `transform`/`filter`
  // (ex.: Popper/Grow), que quebrariam o posicionamento `fixed` e descentralizariam o modal.
  return ReactDOM.createPortal(
    <DialogContext.Provider value={ctx}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        className={cn(
          "fixed inset-0 z-[1300]",
          isPaperScroll ? "overflow-hidden" : "overflow-y-auto",
          className
        )}
        {...other}
      >
        {/* Backdrop */}
        <div
          {...BackdropProps}
          aria-hidden="true"
          className={cn(
            "fixed inset-0 bg-black/50 transition-opacity",
            BackdropProps?.className
          )}
        />

        {/* Container que centraliza o paper + recebe o clique do backdrop. */}
        <div
          onClick={handleBackdropClick}
          className={cn(
            "fixed inset-0 flex outline-none",
            // Center vertical+horizontal; em scroll body, justifica top.
            isPaperScroll ? "items-center justify-center" : "items-start justify-center",
            // Em scroll body o container precisa rolar.
            !isPaperScroll && "overflow-y-auto"
          )}
        >
          {/* Paper */}
          <div
            {...PaperProps}
            onClick={(e) => {
              e.stopPropagation();
              PaperProps?.onClick?.(e);
            }}
            className={cn(
              // Paper base + sombra alta (MUI Dialog usa elevation 24).
              "relative bg-white rounded shadow-2xl m-8 animate-modal-in",
              "flex flex-col",

              // Largura
              fullWidth && !fullScreen && "w-[calc(100%-64px)]",
              !fullScreen && maxWidth !== false && maxWidthMap[maxWidth],

              // Altura/scroll
              isPaperScroll && !fullScreen && "max-h-[calc(100%-64px)]",

              // FullScreen ocupa toda a tela sem margens nem borda arredondada.
              fullScreen && "w-full h-full max-w-none max-h-none m-0 rounded-none",

              PaperProps?.className
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </DialogContext.Provider>,
    document.body
  );
});

Dialog.displayName = "Dialog";

export default Dialog;
